import { useState, useEffect } from 'react';
import { getOrders, getCustomers, getCustomer, getSite, getSites, updateOrderStatus, updateOrderItemStatus, createPartialDispatch, createFollowup, getUsers, getInquiry } from '../services/api';
import CustomDropdown from './CustomDropdown';
import CommentSection from './CommentSection';
import { useResponsive } from '../hooks/useResponsive';
import { getModalStyles } from '../utils/responsiveStyles';
import { generateDeliveryChallan } from '../utils/deliveryChallan';
import { generatePostDeliveryChallan } from '../utils/postDeliveryChallan';

function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [collapsedStages, setCollapsedStages] = useState(new Set());
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPartialDispatchModal, setShowPartialDispatchModal] = useState(false);
  const [partialDispatchOrder, setPartialDispatchOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeliveryInfoModal, setShowDeliveryInfoModal] = useState(false);
  const [deliveryInfoOrder, setDeliveryInfoOrder] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState({
    vehicleNumber: '',
    driverName: '',
    driverPhone: '',
    deliveryDate: new Date().toISOString().split('T')[0],
    transportCharge: '',
    loadingUnloadingCharge: '',
    loadingGst: 18
  });
  const [users, setUsers] = useState([]);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [followupFormData, setFollowupFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    assignedTo: '',
    referenceType: 'order',
    referenceId: '',
    referenceNumber: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');

  const { isMobile } = useResponsive();
  const modalStyles = getModalStyles(isMobile);

  useEffect(() => {
    loadOrders();
    loadCustomers();
    loadSites();
    loadUsers();
  }, []);

  const loadOrders = async () => {
    try {
      const data = await getOrders();

      // Calculate totals for each order
      const ordersWithTotals = data.map(order => {
        // For parent orders with partial dispatches, calculate based on pending quantities
        // For sub-orders and regular orders, use actual quantities
        const subtotal = order.items?.reduce((sum, item) => {
          const rate = parseFloat(item.rate || item.unitPrice) || 0;
          const quantity = parseFloat(item.pendingQuantity) || 0;
          return sum + (rate * quantity);
        }, 0) || 0;

        const taxRate = order.items?.[0]?.gstPercentage || 0;
        const tax = order.inquiryType === 'common' || !order.inquiryType ? (subtotal * taxRate / 100) : 0;
        const total = subtotal + tax;

        return {
          ...order,
          totals: {
            subtotal,
            tax,
            taxRate,
            total
          }
        };
      });

      setOrders(ordersWithTotals);
    } catch (error) {
      alert('Error loading orders: ' + error.message);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadSites = async () => {
    try {
      const data = await getSites();
      setSites(data);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.name : 'Unknown';
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? (site.site_name || site.name) : null;
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    const order = orders.find(o => o.id === orderId);

    if (!order) {
      alert('Order not found');
      return;
    }

    // Don't allow moving from closed or cancelled (except the buttons shouldn't show anyway)
    if (order.status === 'closed' || order.status === 'cancelled') {
      alert('Cannot move closed or cancelled orders');
      return;
    }

    // Get step numbers for validation (except cancelled which is allowed from any stage)
    if (newStatus !== 'cancelled') {
      const currentStage = getStageInfo(order.status);
      const targetStage = getStageInfo(newStatus);

      if (targetStage.step < currentStage.step) {
        alert('Orders cannot move backwards in the pipeline');
        return;
      }
    }

    // Check if moving from ready_for_dispatch to dispatched
    if (order.status === 'ready_for_dispatch' && newStatus === 'dispatched') {
      // Show delivery info modal with prefilled data if available
      setDeliveryInfoOrder({ orderId, newStatus });
      setDeliveryInfo({
        vehicleNumber: order.vehicleNumber || '',
        driverName: order.driverName || '',
        driverPhone: order.driverPhone || '',
        deliveryDate: order.deliveryDate ? new Date(order.deliveryDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        transportCharge: order.transportCharge || '',
        loadingUnloadingCharge: order.loadingUnloadingCharge || '',
        loadingGst: order.loadingGst || 18
      });
      setShowDeliveryInfoModal(true);
      return;
    }

    try {
      await updateOrderStatus(orderId, newStatus);
      loadOrders();
    } catch (error) {
      alert('Error updating order status: ' + error.message);
    }
  };

  const handleDeliveryInfoSubmit = async () => {
    if (!deliveryInfoOrder) return;

    // Validate required fields
    if (!deliveryInfo.vehicleNumber?.trim()) {
      alert('Vehicle Number is required');
      return;
    }
    if (!deliveryInfo.driverName?.trim()) {
      alert('Driver Name is required');
      return;
    }
    if (!deliveryInfo.driverPhone?.trim()) {
      alert('Driver Phone is required');
      return;
    }
    if (!deliveryInfo.deliveryDate) {
      alert('Delivery Date is required');
      return;
    }

    try {
      await updateOrderStatus(deliveryInfoOrder.orderId, deliveryInfoOrder.newStatus, deliveryInfo);
      setShowDeliveryInfoModal(false);
      setDeliveryInfoOrder(null);
      setDeliveryInfo({
        vehicleNumber: '',
        driverName: '',
        driverPhone: '',
        deliveryDate: new Date().toISOString().split('T')[0],
        transportCharge: '',
        loadingCharge: '',
        unloadingCharge: ''
      });
      loadOrders();
      alert('Order dispatched successfully! Delivery challan has been generated.');
    } catch (error) {
      alert('Error dispatching order: ' + error.message);
    }
  };

  const handlePartialDispatch = (order) => {
    setPartialDispatchOrder(order);
    const items = order.items.map(item => ({
      ...item,
      selectedQuantity: 0
    }));
    setSelectedItems(items);
    setShowPartialDispatchModal(true);
  };

  const handlePartialDispatchSubmit = async () => {
    if (!partialDispatchOrder) return;

    const itemsToDispatch = selectedItems
      .filter(item => item.selectedQuantity > 0)
      .map(item => ({
        itemId: item.id,
        quantity: item.selectedQuantity,
        remarks: item.remarks || ''
      }));

    if (itemsToDispatch.length === 0) {
      alert('Please select at least one item with quantity greater than 0');
      return;
    }

    // Validate required delivery information
    if (!deliveryInfo.vehicleNumber?.trim()) {
      alert('Vehicle Number is required');
      return;
    }
    if (!deliveryInfo.driverName?.trim()) {
      alert('Driver Name is required');
      return;
    }
    if (!deliveryInfo.driverPhone?.trim()) {
      alert('Driver Phone is required');
      return;
    }
    if (!deliveryInfo.deliveryDate) {
      alert('Delivery Date is required');
      return;
    }

    try {
      const response = await createPartialDispatch(partialDispatchOrder.id, {
        items: itemsToDispatch,
        vehicleNumber: deliveryInfo.vehicleNumber,
        driverName: deliveryInfo.driverName,
        driverPhone: deliveryInfo.driverPhone,
        deliveryDate: deliveryInfo.deliveryDate,
        remarks: 'Partial dispatch'
      });

      setShowPartialDispatchModal(false);
      setPartialDispatchOrder(null);
      setSelectedItems([]);
      setDeliveryInfo({
        vehicleNumber: '',
        driverName: '',
        driverPhone: '',
        deliveryDate: new Date().toISOString().split('T')[0],
        transportCharge: '',
        loadingCharge: '',
        unloadingCharge: ''
      });
      loadOrders();

      // Show appropriate message based on whether challan was created
      if (response.challan) {
        alert(`Partial dispatch created successfully!\n\nSub-order: ${response.subOrder.orderNumber}\nDelivery Challan: ${response.challan.challanNumber}`);
      } else {
        alert(`Partial dispatch created successfully!\n\nSub-order: ${response.subOrder.orderNumber}\nStatus: Ready for Dispatch`);
      }
    } catch (error) {
      alert('Error creating partial dispatch: ' + error.message);
    }
  };

  const updateSelectedItemQuantity = (itemId, quantity) => {
    setSelectedItems(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, selectedQuantity: Math.min(Math.max(0, quantity), item.pendingQuantity) }
          : item
      )
    );
  };

  const handleItemStatusUpdate = async (orderId, itemId, newStatus) => {
    try {
      await updateOrderItemStatus(orderId, itemId, newStatus);
      loadOrders();
    } catch (error) {
      alert('Error updating item status: ' + error.message);
    }
  };

  const toggleOrderExpanded = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleDragStart = (e, order) => {
    setDraggedOrder(order);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragEnd = () => {
    setDraggedOrder(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnKey);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, columnKey) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedOrder || draggedOrder.status === columnKey) {
      return;
    }

    // Don't allow moving from closed or cancelled
    if (draggedOrder.status === 'closed' || draggedOrder.status === 'cancelled') {
      alert('Cannot move closed or cancelled orders');
      return;
    }

    // Get step numbers for current and target status
    const currentStage = getStageInfo(draggedOrder.status);
    const targetStage = getStageInfo(columnKey);

    // Don't allow moving backwards (except to cancelled which is allowed from any stage)
    if (columnKey !== 'cancelled' && targetStage.step < currentStage.step) {
      alert('Orders cannot move backwards in the pipeline. Use the buttons to move orders forward.');
      return;
    }

    // Special validation: ready_for_dispatch to dispatched requires delivery info
    if (draggedOrder.status === 'ready_for_dispatch' && columnKey === 'dispatched') {
      setDeliveryInfoOrder({ orderId: draggedOrder.id, newStatus: columnKey });
      setDeliveryInfo({
        vehicleNumber: draggedOrder.vehicleNumber || '',
        driverName: draggedOrder.driverName || '',
        driverPhone: draggedOrder.driverPhone || '',
        deliveryDate: draggedOrder.deliveryDate ? new Date(draggedOrder.deliveryDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        transportCharge: draggedOrder.transportCharge || '',
        loadingUnloadingCharge: draggedOrder.loadingUnloadingCharge || '',
        loadingGst: draggedOrder.loadingGst || 18
      });
      setShowDeliveryInfoModal(true);
      return;
    }

    try {
      await updateOrderStatus(draggedOrder.id, columnKey);
      loadOrders();
    } catch (error) {
      alert('Error updating order status: ' + error.message);
    }
  };

  const handleCreateFollowup = (order) => {
    setFollowupFormData({
      title: `Follow up on order ${order.orderNumber}`,
      description: '',
      dueDate: '',
      assignedTo: '',
      referenceType: 'order',
      referenceId: order.id,
      referenceNumber: order.orderNumber
    });
    setShowFollowupModal(true);
  };

  const handleFollowupSubmit = async (e) => {
    e.preventDefault();
    try {
      await createFollowup(followupFormData);
      setShowFollowupModal(false);
      setFollowupFormData({
        title: '',
        description: '',
        dueDate: '',
        assignedTo: '',
        referenceType: 'order',
        referenceId: '',
        referenceNumber: ''
      });
      alert('Follow-up created successfully!');
    } catch (error) {
      alert('Error creating follow-up: ' + error.message);
    }
  };

  const handleDownloadChallan = async (order) => {
    try {
      // Fetch customer and site data
      const customer = await getCustomer(order.customerId);
      const site = await getSite(order.siteId);

      // Generate and download the PDF
      generateDeliveryChallan(order, customer, site);
    } catch (error) {
      alert('Error generating delivery challan: ' + error.message);
    }
  };

  const handleDownloadPostDeliveryChallan = async (order) => {
    try {
      // Fetch customer and site data
      const customer = await getCustomer(order.customerId);
      const site = await getSite(order.siteId);

      // Generate and download the PDF
      generatePostDeliveryChallan(order, customer, site);
    } catch (error) {
      alert('Error generating post-delivery challan: ' + error.message);
    }
  };

  const getStageInfo = (status) => {
    const stages = {
      preparing: { label: 'Being Prepared', color: '#f39c12', step: 1 },
      ready_for_dispatch: { label: 'Ready for Dispatch', color: '#3498db', step: 2 },
      dispatched: { label: 'Dispatched', color: '#9b59b6', step: 3 },
      delivered: { label: 'Delivered', color: '#27ae60', step: 4 },
      payment_pending: { label: 'Payment Pending', color: '#e67e22', step: 5 },
      closed: { label: 'Order Closed', color: '#2c3e50', step: 6 },
      cancelled: { label: 'Cancelled', color: '#e74c3c', step: 0 }
    };
    return stages[status] || { label: status, color: '#95a5a6', step: 0 };
  };

  const getNextStatus = (currentStatus) => {
    const flow = {
      preparing: 'ready_for_dispatch',
      ready_for_dispatch: 'dispatched',
      dispatched: 'delivered',
      delivered: 'payment_pending',
      payment_pending: 'closed'
    };
    return flow[currentStatus];
  };

  const getItemStatusBadge = (status) => {
    const info = getStageInfo(status);
    return (
      <span style={{
        padding: '2px 8px',
        background: info.color,
        color: 'white',
        borderRadius: '8px',
        fontSize: '10px',
        fontWeight: 'bold'
      }}>
        {info.label}
      </span>
    );
  };

  const hasPartialStatus = (order) => {
    if (!order.items || order.items.length <= 1) return false;
    const statuses = order.items.map(item => item.status);
    return new Set(statuses).size > 1;
  };

  const getStatusBadge = (status) => {
    const info = getStageInfo(status);

    return (
      <span style={{
        padding: '6px 12px',
        background: info.color,
        color: 'white',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold'
      }}>
        {info.label}
      </span>
    );
  };

  const renderStageTracker = (order) => {
    const currentStage = getStageInfo(order.status);
    const allStages = [
      { key: 'preparing', label: 'Preparing', step: 1 },
      { key: 'ready_for_dispatch', label: 'Ready', step: 2 },
      { key: 'dispatched', label: 'Dispatched', step: 3 },
      { key: 'delivered', label: 'Delivered', step: 4 },
      { key: 'payment_pending', label: 'Payment', step: 5 },
      { key: 'closed', label: 'Closed', step: 6 }
    ];

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: '8px'
      }}>
        {allStages.map((stage, index) => {
          const isActive = stage.step <= currentStage.step;
          const isCurrent = stage.key === order.status;

          return (
            <div key={stage.key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: isActive ? getStageInfo(stage.key).color : '#ecf0f1',
                border: isCurrent ? '3px solid #3D1F1F' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                color: isActive ? 'white' : '#95a5a6',
                transition: 'all 0.3s ease'
              }}>
                {stage.step}
              </div>
              {index < allStages.length - 1 && (
                <div style={{
                  width: '30px',
                  height: '2px',
                  background: isActive ? '#27ae60' : '#ecf0f1',
                  transition: 'all 0.3s ease'
                }} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const buttonStyle = {
    padding: '6px 12px',
    background: '#C8342B',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  };

  const columns = [
    { key: 'preparing', label: 'Order Being Prepared', color: '#f39c12' },
    { key: 'ready_for_dispatch', label: 'Ready for Dispatch', color: '#3498db' },
    { key: 'dispatched', label: 'Dispatched', color: '#9b59b6' },
    { key: 'delivered', label: 'Delivered', color: '#27ae60' },
    { key: 'payment_pending', label: 'Payment Pending', color: '#e67e22' },
    { key: 'closed', label: 'Order Closed', color: '#2c3e50' },
    { key: 'cancelled', label: 'Cancelled', color: '#e74c3c' }
  ];

  const getFilteredOrders = () => {
    let filtered = orders;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(order => {
        const customerName = getCustomerName(order.customerId).toLowerCase();
        const orderNumber = (order.orderNumber || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();

        // Search in order number, customer name, and items
        const matchesBasic = orderNumber.includes(searchLower) || customerName.includes(searchLower);

        // Search in items
        const matchesItems = order.items?.some(item =>
          (item.productName || '').toLowerCase().includes(searchLower) ||
          (item.specification || '').toLowerCase().includes(searchLower)
        );

        return matchesBasic || matchesItems;
      });
    }

    // Month filter
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        const [year, month] = selectedMonth.split('-');
        return orderDate.getFullYear() === parseInt(year) &&
               orderDate.getMonth() === parseInt(month) - 1;
      });
    }

    return filtered;
  };

  const getOrdersByStatus = (status) => {
    const filtered = getFilteredOrders();
    return filtered.filter(order => order.status === status);
  };

  const toggleStageCollapse = (stageKey) => {
    const newCollapsed = new Set(collapsedStages);
    if (newCollapsed.has(stageKey)) {
      newCollapsed.delete(stageKey);
    } else {
      newCollapsed.add(stageKey);
    }
    setCollapsedStages(newCollapsed);
  };

  const renderOrderCard = (order) => {
    const nextStatus = getNextStatus(order.status);
    const isExpanded = expandedOrders.has(order.id);
    const isPartial = hasPartialStatus(order);
    const isDragging = draggedOrder?.id === order.id;
    const isDraggable = order.status !== 'closed' && order.status !== 'cancelled';

    return (
      <div
        key={order.id}
        draggable={isDraggable}
        onDragStart={(e) => isDraggable ? handleDragStart(e, order) : e.preventDefault()}
        onDragEnd={handleDragEnd}
        onClick={async () => {
          setSelectedOrder(order);
          setShowDetailModal(true);
          // Fetch site information if available
          if (order.siteId) {
            try {
              const site = await getSite(order.siteId);
              setSelectedSite(site);
            } catch (error) {
              console.error('Error fetching site:', error);
              setSelectedSite(null);
            }
          } else {
            setSelectedSite(null);
          }
          // Fetch inquiry information if available
          if (order.inquiryId) {
            try {
              const inquiry = await getInquiry(order.inquiryId);
              setSelectedInquiry(inquiry);
            } catch (error) {
              console.error('Error fetching inquiry:', error);
              setSelectedInquiry(null);
            }
          } else {
            setSelectedInquiry(null);
          }
        }}
        style={{
          background: 'white',
          borderRadius: '8px',
          padding: '15px',
          width: '280px',
          flexShrink: 0,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: isPartial ? '2px solid #f39c12' : '1px solid #ecf0f1',
          transition: 'all 0.2s ease',
          opacity: isDragging ? 0.5 : 1,
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}>
        <div style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#3D1F1F', fontSize: '14px' }}>
              {order.orderNumber}
            </div>
            {isPartial && (
              <span style={{
                padding: '2px 8px',
                background: '#f39c12',
                color: 'white',
                borderRadius: '8px',
                fontSize: '9px',
                fontWeight: 'bold'
              }}>
                PARTIAL
              </span>
            )}
          </div>
          <div style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '4px' }}>
            {getCustomerName(order.customerId)}
          </div>
          {order.siteId && getSiteName(order.siteId) && (
            <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '2px' }}>
              Site: {getSiteName(order.siteId)}
            </div>
          )}
          {order.inquiryNumber && (
            <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '2px' }}>
              Ref: {order.inquiryNumber}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #ecf0f1' }}>
          {order.items && order.items.length > 0 && (
            <>
              {order.items.slice(0, 2).map((item, idx) => (
                <div key={idx} style={{ fontSize: '12px', color: '#555', marginBottom: '3px' }}>
                  • {item.productName} ({item.pendingQuantity || item.quantity} {item.unit})
                </div>
              ))}
              {order.items.length > 2 && (
                <div style={{ fontSize: '11px', color: '#7f8c8d', fontStyle: 'italic' }}>
                  +{order.items.length - 2} more items
                </div>
              )}
            </>
          )}
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#27ae60' }}>
            ₹{order.totals?.total?.toFixed(2) || '0.00'}
          </div>
          <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
            {new Date(order.createdAt).toLocaleDateString()}
          </div>
        </div>

        {nextStatus && order.status !== 'delivered' && order.status !== 'cancelled' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleStatusUpdate(order.id, nextStatus);
            }}
            style={{
              ...buttonStyle,
              width: '100%',
              background: '#27ae60',
              padding: '8px 12px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#229954'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#27ae60'}
          >
            Move All to {getStageInfo(nextStatus).label} →
          </button>
        )}

        {order.status === 'delivered' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleStatusUpdate(order.id, 'payment_pending');
            }}
            style={{
              ...buttonStyle,
              width: '100%',
              background: '#e67e22',
              padding: '8px 12px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#d35400'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#e67e22'}
          >
            Move to Payment Pending →
          </button>
        )}

        {order.status === 'ready_for_dispatch' && !order.parentOrderId && (
          <button
            onClick={() => handlePartialDispatch(order)}
            style={{
              ...buttonStyle,
              width: '100%',
              background: '#f39c12',
              padding: '8px 12px',
              marginTop: '6px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e67e22'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f39c12'}
          >
            Partial Dispatch
          </button>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCreateFollowup(order);
          }}
          style={{
            ...buttonStyle,
            width: '100%',
            background: '#16a085',
            padding: '8px 12px',
            marginTop: '6px'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#138d75'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#16a085'}
        >
          Create Follow-up
        </button>

        {order.status === 'dispatched' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadChallan(order);
            }}
            style={{
              ...buttonStyle,
              width: '100%',
              background: '#8e44ad',
              padding: '8px 12px',
              marginTop: '6px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#7d3c98'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#8e44ad'}
          >
            Download Challan
          </button>
        )}

        {order.status === 'delivered' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadPostDeliveryChallan(order);
            }}
            style={{
              ...buttonStyle,
              width: '100%',
              background: '#27ae60',
              padding: '8px 12px',
              marginTop: '6px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#229954'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#27ae60'}
          >
            Download Post-Delivery Challan
          </button>
        )}

        {order.status !== 'closed' && order.status !== 'cancelled' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Are you sure you want to cancel order ${order.orderNumber}?`)) {
                handleStatusUpdate(order.id, 'cancelled');
              }
            }}
            style={{
              ...buttonStyle,
              width: '100%',
              background: '#e74c3c',
              padding: '8px 12px',
              marginTop: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#c0392b'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#e74c3c'}
          >
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>×</span>
            Cancel Order
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? '15px' : '20px' }}>
        <h1 style={{ color: '#3D1F1F', fontSize: isMobile ? '20px' : '28px' }}>Orders {!isMobile && 'Kanban Board'}</h1>
      </div>

      {/* Search and Filter Bar */}
      <div style={{
        background: 'white',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <div style={{ flex: isMobile ? '1 1 100%' : 2 }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#7f8c8d'
          }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Search by order number, customer, or product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
        </div>
        <div style={{ flex: isMobile ? '1 1 100%' : 1 }}>
          <label style={{
            display: 'block',
            marginBottom: '5px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#7f8c8d'
          }}>
            Month
          </label>
          <CustomDropdown
            value={selectedMonth}
            onChange={(value) => setSelectedMonth(value)}
            options={[
              { value: 'all', label: 'All Months' },
              ...(() => {
                const months = [];
                const now = new Date();
                for (let i = 0; i < 12; i++) {
                  const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                  months.push({ value: `${year}-${month}`, label });
                }
                return months;
              })()
            ]}
            placeholder="All Months"
          />
        </div>
      </div>

      {orders.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: isMobile ? '20px' : '40px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ color: '#7f8c8d', fontSize: isMobile ? '14px' : '16px' }}>
            No orders found. Orders are created from accepted quotations.
          </p>
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px'
        }}>
          {columns.map(column => {
            const ordersInColumn = getOrdersByStatus(column.key);
            const isDropTarget = dragOverColumn === column.key;
            const isCollapsed = collapsedStages.has(column.key);

            return (
              <div
                key={column.key}
                onDragOver={(e) => handleDragOver(e, column.key)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.key)}
                style={{
                  background: isDropTarget ? '#e8f4f8' : 'white',
                  borderRadius: '8px',
                  border: isDropTarget ? '2px dashed #3498db' : '1px solid #e0e0e0',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                <div
                  onClick={() => toggleStageCollapse(column.key)}
                  style={{
                    padding: '15px',
                    borderBottom: isCollapsed ? 'none' : `3px solid ${column.color}`,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: isCollapsed ? '#f8f9fa' : 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    borderRadius: '8px 8px 0 0'
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: column.color
                    }}></div>
                    <h3 style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#3D1F1F'
                    }}>
                      {column.label}
                    </h3>
                    <div style={{
                      fontSize: '13px',
                      color: '#7f8c8d',
                      fontWeight: '600',
                      background: '#f0f0f0',
                      padding: '2px 8px',
                      borderRadius: '12px'
                    }}>
                      {ordersInColumn.length}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '18px',
                    color: '#7f8c8d',
                    transition: 'transform 0.2s',
                    transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'
                  }}>
                    ▼
                  </div>
                </div>

                {!isCollapsed && (
                  <div style={{
                    padding: '15px'
                  }}>
                    {ordersInColumn.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        color: '#7f8c8d',
                        fontSize: '14px'
                      }}>
                        No orders in this stage
                      </div>
                    ) : (
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: '12px'
                      }}>
                        {ordersInColumn.map(order => renderOrderCard(order))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showDetailModal && selectedOrder && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => {
            setShowDetailModal(false);
            setSelectedSite(null);
            setSelectedInquiry(null);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#3D1F1F' }}>Order Details</h2>
              <button
                onClick={() => {
            setShowDetailModal(false);
            setSelectedSite(null);
            setSelectedInquiry(null);
          }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#7f8c8d',
                  padding: '0',
                  width: '30px',
                  height: '30px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3D1F1F'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#7f8c8d'}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '2px solid #ecf0f1' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Order Number</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#3D1F1F' }}>
                    {selectedOrder.orderNumber}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Status</div>
                  {getStatusBadge(selectedOrder.status)}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Customer</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                    {getCustomerName(selectedOrder.customerId)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Order Date</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                    {new Date(selectedOrder.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              </div>

              {selectedSite && (
                <div style={{ marginTop: '15px' }}>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Site</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                    {selectedSite.site_name || selectedSite.name}
                  </div>
                  {selectedSite.address && (
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                      {selectedSite.address}
                    </div>
                  )}
                </div>
              )}

              {selectedOrder.inquiryNumber && (
                <div style={{ marginTop: '15px' }}>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '4px' }}>Inquiry Reference</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F' }}>
                    {selectedOrder.inquiryNumber}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', color: '#3D1F1F', marginBottom: '12px' }}>Progress Tracker</h3>
              {renderStageTracker(selectedOrder)}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', color: '#3D1F1F', marginBottom: '12px' }}>Order Items</h3>
              {selectedOrder.items && selectedOrder.items.map((item, idx) => (
                <div key={idx} style={{
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  marginBottom: '10px',
                  border: '1px solid #e9ecef'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', marginBottom: '4px' }}>
                        {item.productName}
                      </div>
                      <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                        Quantity: {item.pendingQuantity || item.quantity} {item.unit}
                        {item.deliveredQuantity > 0 && (
                          <span style={{ fontSize: '11px', color: '#e67e22', marginLeft: '8px' }}>
                            (Delivered: {item.deliveredQuantity})
                          </span>
                        )}
                      </div>
                    </div>
                    {getItemStatusBadge(item.status)}
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', marginTop: '8px' }}>
                    <div>Rate: ₹{item.rate?.toFixed(2) || '0.00'} per {item.unit}</div>
                    <div style={{ fontWeight: '600', color: '#27ae60', marginTop: '4px' }}>
                      Subtotal: ₹{((parseFloat(item.rate || item.unitPrice) || 0) * (parseFloat(item.pendingQuantity) || 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <h3 style={{ fontSize: '16px', color: '#3D1F1F', marginBottom: '12px' }}>Order Summary</h3>
              <div style={{ fontSize: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#7f8c8d' }}>Subtotal:</span>
                  <span style={{ fontWeight: '600', color: '#3D1F1F' }}>
                    ₹{selectedOrder.totals?.subtotal?.toFixed(2) || '0.00'}
                  </span>
                </div>
                {/* Only show tax for common type inquiries or when inquiry type is not set */}
                {(!selectedOrder.inquiryType || selectedOrder.inquiryType === 'common') && (selectedOrder.totals?.taxRate > 0 || selectedOrder.totals?.tax > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#7f8c8d' }}>Tax ({selectedOrder.totals?.taxRate || 0}%):</span>
                    <span style={{ fontWeight: '600', color: '#3D1F1F' }}>
                      ₹{selectedOrder.totals?.tax?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                )}
                <div style={{ height: '1px', background: '#dee2e6', margin: '10px 0' }}></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#3D1F1F' }}>Total:</span>
                  <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#27ae60' }}>
                    ₹{selectedOrder.totals?.total?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            </div>

            {/* Comment Section */}
            <CommentSection
              referenceType="order"
              referenceId={selectedOrder.id}
              currentUserId={JSON.parse(localStorage.getItem('user') || '{}').id}
              currentUserName={JSON.parse(localStorage.getItem('user') || '{}').name}
            />
          </div>
        </div>
      )}

      {showPartialDispatchModal && partialDispatchOrder && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowPartialDispatchModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '90%',
              maxWidth: '750px',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h2 style={{ margin: 0, color: '#3D1F1F', fontSize: '22px', fontWeight: 'bold' }}>
                  Partial Dispatch - {partialDispatchOrder.orderNumber}
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#7f8c8d' }}>
                  Select items and quantities to dispatch
                </p>
              </div>
              <button
                onClick={() => setShowPartialDispatchModal(false)}
                style={{
                  background: '#f8f9fa',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#7f8c8d',
                  padding: '0',
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e9ecef';
                  e.currentTarget.style.color = '#3D1F1F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                  e.currentTarget.style.color = '#7f8c8d';
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', color: '#3D1F1F', marginBottom: '16px', fontWeight: 'bold' }}>Select Items to Dispatch</h3>
              {selectedItems.map((item, idx) => (
                <div key={idx} style={{
                  padding: '18px',
                  background: '#f8f9fa',
                  borderRadius: '10px',
                  marginBottom: '14px',
                  border: '2px solid #e9ecef',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#dee2e6'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                >
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#3D1F1F', marginBottom: '10px' }}>
                    {item.productName}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#7f8c8d',
                    marginBottom: '14px',
                    background: '#fff',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>
                    Available: <span style={{ fontWeight: '600', color: '#27ae60' }}>{item.pendingQuantity} {item.unit}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', minWidth: '140px' }}>
                      Quantity to dispatch:
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={item.pendingQuantity}
                      value={item.selectedQuantity}
                      onChange={(e) => updateSelectedItemQuantity(item.id, parseInt(e.target.value) || 0)}
                      style={{
                        padding: '10px 14px',
                        border: '2px solid #dee2e6',
                        borderRadius: '8px',
                        fontSize: '14px',
                        width: '120px',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#f39c12'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#dee2e6'}
                    />
                    <span style={{ fontSize: '14px', color: '#7f8c8d', fontWeight: '600' }}>{item.unit}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '20px', paddingTop: '20px', borderTop: '2px solid #ecf0f1' }}>
              <h3 style={{ fontSize: '16px', color: '#3D1F1F', marginBottom: '16px', fontWeight: 'bold' }}>
                Delivery Information <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#7f8c8d' }}>(All fields required)</span>
              </h3>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                  Vehicle Number <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="text"
                  value={deliveryInfo.vehicleNumber}
                  onChange={(e) => setDeliveryInfo({...deliveryInfo, vehicleNumber: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., KA-01-AB-1234"
                  onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                    Driver Name <span style={{ color: '#C8342B' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={deliveryInfo.driverName}
                    onChange={(e) => setDeliveryInfo({...deliveryInfo, driverName: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'border-color 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Driver name"
                    onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                    Driver Phone <span style={{ color: '#C8342B' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    value={deliveryInfo.driverPhone}
                    onChange={(e) => setDeliveryInfo({...deliveryInfo, driverPhone: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'border-color 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Phone number"
                    onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                  Delivery Date <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="date"
                  value={deliveryInfo.deliveryDate}
                  onChange={(e) => setDeliveryInfo({...deliveryInfo, deliveryDate: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e9ecef' }}>
              <button
                onClick={() => setShowPartialDispatchModal(false)}
                style={{
                  padding: '12px 24px',
                  background: '#f8f9fa',
                  color: '#495057',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
              >
                Cancel
              </button>
              <button
                onClick={handlePartialDispatchSubmit}
                style={{
                  padding: '12px 24px',
                  background: '#f39c12',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(243, 156, 18, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e67e22';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(243, 156, 18, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f39c12';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(243, 156, 18, 0.3)';
                }}
              >
                Create Partial Dispatch
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeliveryInfoModal && deliveryInfoOrder && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowDeliveryInfoModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '90%',
              maxWidth: '550px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h2 style={{ margin: 0, color: '#3D1F1F', fontSize: '22px', fontWeight: 'bold' }}>Delivery Information</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#7f8c8d' }}>All fields are required</p>
              </div>
              <button
                onClick={() => setShowDeliveryInfoModal(false)}
                style={{
                  background: '#f8f9fa',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#7f8c8d',
                  padding: '0',
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e9ecef';
                  e.currentTarget.style.color = '#3D1F1F';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8f9fa';
                  e.currentTarget.style.color = '#7f8c8d';
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                  Vehicle Number <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="text"
                  value={deliveryInfo.vehicleNumber}
                  onChange={(e) => setDeliveryInfo({...deliveryInfo, vehicleNumber: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., KA-01-AB-1234"
                  onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px', marginBottom: '18px' }}>
                <div>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                    Driver Name <span style={{ color: '#C8342B' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={deliveryInfo.driverName}
                    onChange={(e) => setDeliveryInfo({...deliveryInfo, driverName: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'border-color 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Driver name"
                    onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                    Driver Phone <span style={{ color: '#C8342B' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    value={deliveryInfo.driverPhone}
                    onChange={(e) => setDeliveryInfo({...deliveryInfo, driverPhone: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'border-color 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Phone number"
                    onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                  Delivery Date <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="date"
                  value={deliveryInfo.deliveryDate}
                  onChange={(e) => setDeliveryInfo({...deliveryInfo, deliveryDate: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'border-color 0.2s',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                />
              </div>

              <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '2px solid #f1f3f5' }}>
                <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#3D1F1F', marginBottom: '16px', marginTop: 0 }}>Additional Charges</h3>
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                    Transport Charge
                  </label>
                  <input
                    type="number"
                    value={deliveryInfo.transportCharge}
                    onChange={(e) => setDeliveryInfo({...deliveryInfo, transportCharge: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'border-color 0.2s',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                      Loading/Unloading Charge
                    </label>
                    <input
                      type="number"
                      value={deliveryInfo.loadingUnloadingCharge}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDeliveryInfo({
                          ...deliveryInfo,
                          loadingUnloadingCharge: value
                        });
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '14px',
                        transition: 'border-color 0.2s',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#3D1F1F', display: 'block', marginBottom: '8px' }}>
                      Loading GST %
                    </label>
                    <input
                      type="number"
                      value={deliveryInfo.loadingGst}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDeliveryInfo({
                          ...deliveryInfo,
                          loadingGst: value
                        });
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '2px solid #e9ecef',
                        borderRadius: '8px',
                        fontSize: '14px',
                        transition: 'border-color 0.2s',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                      placeholder="18"
                      min="0"
                      max="100"
                      step="0.01"
                      onFocus={(e) => e.currentTarget.style.borderColor = '#C8342B'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e9ecef'}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e9ecef' }}>
              <button
                onClick={() => setShowDeliveryInfoModal(false)}
                style={{
                  padding: '12px 24px',
                  background: '#f8f9fa',
                  color: '#495057',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
              >
                Cancel
              </button>
              <button
                onClick={handleDeliveryInfoSubmit}
                style={{
                  padding: '12px 24px',
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#229954';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(39, 174, 96, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#27ae60';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(39, 174, 96, 0.3)';
                }}
              >
                Dispatch Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowupModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => {
            setShowFollowupModal(false);
            setFollowupFormData({
              title: '',
              description: '',
              dueDate: '',
              assignedTo: '',
              referenceType: 'order',
              referenceId: '',
              referenceNumber: ''
            });
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, color: '#3D1F1F' }}>Create Follow-up</h2>
            <form onSubmit={handleFollowupSubmit}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Title <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="text"
                  value={followupFormData.title}
                  onChange={(e) => setFollowupFormData({ ...followupFormData, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Description
                </label>
                <textarea
                  value={followupFormData.description}
                  onChange={(e) => setFollowupFormData({ ...followupFormData, description: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Due Date <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <input
                  type="date"
                  value={followupFormData.dueDate}
                  onChange={(e) => setFollowupFormData({ ...followupFormData, dueDate: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Assigned To <span style={{ color: '#C8342B' }}>*</span>
                </label>
                <CustomDropdown
                  value={followupFormData.assignedTo}
                  onChange={(value) => setFollowupFormData({ ...followupFormData, assignedTo: value })}
                  options={users.map(user => ({ value: user.id, label: user.name }))}
                  placeholder="Select User"
                  required
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                  Reference
                </label>
                <input
                  type="text"
                  value={followupFormData.referenceNumber}
                  disabled
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '14px',
                    background: '#f8f9fa',
                    color: '#7f8c8d'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowFollowupModal(false);
                    setFollowupFormData({
                      title: '',
                      description: '',
                      dueDate: '',
                      assignedTo: '',
                      referenceType: 'order',
                      referenceId: '',
                      referenceNumber: ''
                    });
                  }}
                  style={{
                    ...buttonStyle,
                    background: '#95a5a6'
                  }}
                >
                  Cancel
                </button>
                <button type="submit" style={buttonStyle}>
                  Create Follow-up
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Orders;
